import { ConnectorStatus } from "@/components/connectors/ConnectorStatus";
import { connectorLoadState } from "@/stores/connectors/loading";
import { useId, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Plug } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { AuthPageHeader, AuthPageLayout } from "@/components/auth/AuthPageLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  completeConsent,
  ConnectorError,
  connectorOrigin,
  loadConsent,
  submitConsent,
  type ConnectorScope,
} from "@/stores/connectors/repository";

function useConsentForm(oauthQuery: string) {
  const { userId, userProfile } = useAuth();
  const requestId = useId();
  const [organization, setOrganization] = useState("");
  const [scopes, setScopes] = useState<ConnectorScope[]>(["franklin.read"]);
  const [leaving, setLeaving] = useState(false);
  const query = useQuery({
    // The signed authorization request must never appear in cache identifiers or logs.
    queryKey: ["connector-consent", userId, requestId],
    queryFn: ({ signal }) => loadConsent(oauthQuery, signal),
    enabled: Boolean(connectorOrigin() && oauthQuery),
    retry: false,
    gcTime: 0,
  });
  const mutation = useMutation({
    mutationFn: async (accept: boolean) => {
      const result = await submitConsent(
        accept
          ? { accept, oauth_query: oauthQuery, organization, scopes }
          : { accept: false, oauth_query: oauthQuery }
      );
      completeConsent(result.redirect_uri);
      setLeaving(true);
    },
  });
  const busy = mutation.isPending || leaving;
  const details = query.data;
  const expired =
    !oauthQuery || (query.error instanceof ConnectorError && query.error.status === 400);

  const canAllow =
    details?.organizations.some((org) => org.id === organization) &&
    details?.scopes.includes("franklin.read");
  const accept = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canAllow && !busy) mutation.mutate(true);
  };
  return {
    canAllow,
    accept,
    organization,
    setOrganization,
    scopes,
    setScopes,
    query,
    mutation,
    busy,
    details,
    expired,
    userProfile,
  };
}

type ConsentModel = ReturnType<typeof useConsentForm>;

function ConsentForm({ oauthQuery }: { oauthQuery: string }) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const model = useConsentForm(oauthQuery);
  const { accept, query, details, expired } = model;
  const loadState = connectorLoadState(Boolean(connectorOrigin()), query, expired);
  if (loadState || !details)
    return <ConnectorStatus state={loadState ?? "failed"} retry={() => void query.refetch()} />;

  return (
    <form className="space-y-6" onSubmit={accept}>
      <ConsentIdentity model={{ ...model, details }} />
      <p className="text-sm text-muted-foreground">{copy.clientNotice}</p>
      <OrganizationChoice model={{ ...model, details }} />
      <ConsentPermissions model={{ ...model, details }} />
      <ConsentDecision model={{ ...model, details }} />
      <Link
        to="/settings?section=connections"
        className="block rounded-sm text-sm text-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {copy.settings}
      </Link>
    </form>
  );
}

function ConsentIdentity({
  model,
}: {
  model: ConsentModel & { details: NonNullable<ConsentModel["details"]> };
}) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const { details, userProfile } = model;
  return (
    <dl className="space-y-2 break-words text-sm">
      <div>
        <dt className="text-muted-foreground">{copy.account}</dt>
        <dd>{userProfile?.email}</dd>
      </div>
      <div>
        <dt className="text-muted-foreground">{copy.client}</dt>
        <dd className="font-semibold">{details.client.client_name || copy.unnamed}</dd>
      </div>
      <div>
        <dt className="text-muted-foreground">{copy.clientIdentity}</dt>
        <dd className="break-all text-xs">{details.client.client_id}</dd>
      </div>
    </dl>
  );
}

function OrganizationChoice({
  model,
}: {
  model: ConsentModel & { details: NonNullable<ConsentModel["details"]> };
}) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const { details, organization, setOrganization, busy } = model;
  return (
    <div className="space-y-2">
      <Label htmlFor="connector-organization">{copy.organization}</Label>
      <select
        id="connector-organization"
        value={organization}
        onChange={(event) => setOrganization(event.target.value)}
        disabled={busy || details.organizations.length === 0}
        className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="">{copy.chooseOrganization}</option>
        {details.organizations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
      {details.organizations.length === 0 && (
        <p role="status" className="text-sm text-muted-foreground">
          {copy.noOrganizations}
        </p>
      )}
    </div>
  );
}

function ConsentPermissions({
  model,
}: {
  model: ConsentModel & { details: NonNullable<ConsentModel["details"]> };
}) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const { details, scopes, setScopes, busy } = model;
  return (
    <fieldset className="space-y-3" disabled={busy}>
      <legend className="mb-3 text-sm font-medium">{copy.permissions}</legend>
      {details.scopes.map((scope) => (
        <div key={scope} className="flex items-start gap-3">
          <Checkbox
            id={`scope-${scope}`}
            checked={scopes.includes(scope)}
            disabled={busy || scope === "franklin.read"}
            onCheckedChange={(checked) =>
              setScopes((current) =>
                checked === true ? [...current, scope] : current.filter((item) => item !== scope)
              )
            }
          />
          <Label className="leading-5" htmlFor={`scope-${scope}`}>
            {copy.scopes[scope]}
          </Label>
        </div>
      ))}
    </fieldset>
  );
}

function ConsentDecision({
  model,
}: {
  model: ConsentModel & { details: NonNullable<ConsentModel["details"]> };
}) {
  const { t } = useLanguage();
  const copy = t.connectors;
  const { details, mutation, busy, canAllow } = model;
  return (
    <>
      <p className="text-sm text-muted-foreground">{copy.permissionNotice}</p>
      {details.offline_access && (
        <p className="text-sm text-muted-foreground">{copy.persistentAccess}</p>
      )}
      {mutation.isError && (
        <Alert variant="destructive">
          <AlertDescription>{copy.failed}</AlertDescription>
        </Alert>
      )}
      <div className="flex flex-col gap-3 sm:flex-row-reverse">
        <Button type="submit" className="min-h-11 flex-1" disabled={busy || !canAllow}>
          {busy ? copy.pending : copy.allow}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="min-h-11 flex-1"
          disabled={busy}
          onClick={() => mutation.mutate(false)}
        >
          {copy.deny}
        </Button>
      </div>
    </>
  );
}

export default function ConnectorAuthorize() {
  const { isAuthenticated, isLoading, userId } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const oauthQuery = query.get("oauth_query") ?? location.search.slice(1);
  if (!isLoading && !isAuthenticated)
    return (
      <Navigate to="/login" state={{ returnUrl: location.pathname + location.search }} replace />
    );
  return (
    <AuthPageLayout contentClassName="max-w-lg">
      <AuthPageHeader
        title={t.connectors.title}
        description={t.connectors.description}
        icon={<Plug aria-hidden="true" />}
      />
      {isLoading ? (
        <p role="status">{t.connectors.loading}</p>
      ) : (
        <ConsentForm key={`${userId}:${oauthQuery}`} oauthQuery={oauthQuery} />
      )}
    </AuthPageLayout>
  );
}
